import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useOrganisation } from '../../contexts/OrganisationContext';
import { useProjectTree } from '../../contexts/ProjectTreeContext';
import OrganisationSelector from '../Organisation/OrganisationSelector';
import nubiecloudMark from '../../assets/nubiecloud-mark.png';
import apiService from '../../services/api';
import { Project, Environment, Deployment } from '../../types/api';
import {
  HomeIcon,
  FolderIcon,
  RocketLaunchIcon,
  RectangleStackIcon,
  WrenchScrewdriverIcon,
  ShieldCheckIcon,
  ArchiveBoxIcon,
  CircleStackIcon,
  SignalIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  BuildingOfficeIcon,
  UsersIcon,
  Squares2X2Icon,
  SparklesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';

const SIDEBAR_KEY = 'sidebar-collapsed';

interface NavItemDef {
  nameKey: string;
  href: string;
  icon: React.ForwardRefExoticComponent<React.SVGProps<SVGSVGElement> & { title?: string; titleId?: string }>;
  matchPaths?: string[];
  isProjects?: boolean;
}

const mainNavItemDefs: NavItemDef[] = [
  { nameKey: 'nav.dashboard', href: '/dashboard', icon: HomeIcon },
  { nameKey: 'nav.nubieai', href: '/nubie-ai', icon: SparklesIcon, matchPaths: ['/nubie-ai'] },
  // AUCUN `matchPaths`, et c'est delibere : `isActive` compare par `startsWith`,
  // donc `['/mcp']` allumerait aussi cette entree sur '/mcp/saisie/:id' et
  // '/mcp/confirmation/:id' -- deux parcours ponctuels qu'un AGENT fait ouvrir et
  // qui ne relevent pas de cet ecran. Sans `matchPaths`, seul le chemin EXACT
  // correspond, ce qui est precisement ce qu'on veut ici.
  { nameKey: 'nav.mcp', href: '/mcp', icon: ServerStackIcon },
  { nameKey: 'nav.projects', href: '/projects', icon: FolderIcon, matchPaths: ['/projects', '/environments', '/project'], isProjects: true },
  { nameKey: 'nav.deployments', href: '/deployments', icon: RocketLaunchIcon, matchPaths: ['/deployments', '/deploy', '/deployment'] },
  { nameKey: 'nav.blueprints', href: '/blueprints', icon: RectangleStackIcon },
  { nameKey: 'nav.builds', href: '/builds', icon: WrenchScrewdriverIcon },
  { nameKey: 'nav.backups', href: '/backups', icon: ShieldCheckIcon },
  { nameKey: 'nav.nubis3', href: '/storage', icon: CircleStackIcon },
  { nameKey: 'nav.status', href: '/status', icon: SignalIcon },
  { nameKey: 'nav.auditLogsRoute', href: '/audit-logs', icon: ClipboardDocumentListIcon },
];

const Sidebar: React.FC = () => {
  const { t } = (useTranslation as any)('common');
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrganisation } = useOrganisation();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(SIDEBAR_KEY) === 'true';
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  // Shared project tree state (synced with ProjectsPage)
  const { expandedProjectId, setExpandedProjectId, projectsExpanded, setProjectsExpanded } = useProjectTree();

  // Local sidebar tree state
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectEnvs, setProjectEnvs] = useState<Record<string, Environment[]>>({});
  const [envDeployments, setEnvDeployments] = useState<Record<string, Deployment[]>>({});
  const [expandedEnv, setExpandedEnv] = useState<string | null>(null);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [loadingEnvs, setLoadingEnvs] = useState<Record<string, boolean>>({});
  const [loadingDeps, setLoadingDeps] = useState<Record<string, boolean>>({});

  const loadProjects = useCallback(async () => {
    try {
      setLoadingProjects(true);
      const data = await apiService.getProjects(currentOrganisation?.id);
      setProjects(data);
    } catch {
      // silently fail in sidebar
    } finally {
      setLoadingProjects(false);
    }
  }, [currentOrganisation?.id]);

  const loadEnvsForProject = useCallback(async (projectId: string) => {
    if (projectEnvs[projectId]) return;
    try {
      setLoadingEnvs(prev => ({ ...prev, [projectId]: true }));
      const envs = await apiService.getProjectEnvironments(projectId);
      setProjectEnvs(prev => ({ ...prev, [projectId]: envs }));
    } catch {
      setProjectEnvs(prev => ({ ...prev, [projectId]: [] }));
    } finally {
      setLoadingEnvs(prev => ({ ...prev, [projectId]: false }));
    }
  }, [projectEnvs]);

  const loadDeploymentsForEnv = useCallback(async (envId: string) => {
    if (envDeployments[envId]) return;
    try {
      setLoadingDeps(prev => ({ ...prev, [envId]: true }));
      const deps = await apiService.getEnvironmentDeployments(envId);
      setEnvDeployments(prev => ({ ...prev, [envId]: deps }));
    } catch {
      setEnvDeployments(prev => ({ ...prev, [envId]: [] }));
    } finally {
      setLoadingDeps(prev => ({ ...prev, [envId]: false }));
    }
  }, [envDeployments]);

  const handleToggleProjects = () => {
    const next = !projectsExpanded;
    setProjectsExpanded(next);
    if (next) loadProjects();
  };

  const handleToggleProject = (projectId: string) => {
    if (expandedProjectId === projectId) {
      setExpandedProjectId(null);
      setExpandedEnv(null);
    } else {
      setExpandedProjectId(projectId);
      setExpandedEnv(null);
      loadEnvsForProject(projectId);
    }
  };

  const handleToggleEnv = (envId: string) => {
    if (expandedEnv === envId) {
      setExpandedEnv(null);
    } else {
      setExpandedEnv(envId);
      loadDeploymentsForEnv(envId);
    }
  };

  const getStatusDot = (status: string) => {
    const colors: Record<string, string> = {
      running: 'bg-green-400',
      deployed: 'bg-green-400',
      stopped: 'bg-gray-400',
      failed: 'bg-red-400',
      pending: 'bg-yellow-400',
    };
    return colors[status] || 'bg-gray-400';
  };

  // When expandedProjectId changes from ProjectsPage, auto-expand sidebar tree and load envs
  // When expandedProjectId changes from ProjectsPage, auto-expand sidebar tree and load envs
  useEffect(() => {
    if (expandedProjectId) {
      if (!projectsExpanded) setProjectsExpanded(true);
      if (projects.length === 0) loadProjects();
      loadEnvsForProject(expandedProjectId);
    }
  }, [expandedProjectId]);

  // When org changes, clear cached data and reload projects
  useEffect(() => {
    setProjects([]);
    setProjectEnvs({});
    setEnvDeployments({});
    setExpandedEnv(null);
    if (projectsExpanded) {
      loadProjects();
    }
  }, [currentOrganisation?.id]);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_KEY, String(collapsed));
  }, [collapsed]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (item: NavItemDef) => {
    if (location.pathname === item.href) return true;
    if (item.matchPaths) {
      return item.matchPaths.some(p => location.pathname.startsWith(p));
    }
    return false;
  };

  const bottomNavItems: NavItemDef[] = [
    { nameKey: 'sidebar.settings', href: '/profile', icon: Cog6ToothIcon },
  ];

  // Entree « Organisation » (a cote de Parametres) : trois sous-menus vers les
  // pages de l'organisation courante. Le selecteur en haut ne sert qu'a voir
  // l'organisation courante et a en changer.
  const orgBase = currentOrganisation ? `/organisations/${currentOrganisation.id}` : null;
  const orgSubItems = orgBase ? [
    { nameKey: 'sidebar.orgDashboard', href: `${orgBase}/dashboard`, icon: Squares2X2Icon },
    { nameKey: 'sidebar.orgMembers', href: `${orgBase}/members`, icon: UsersIcon, matchPaths: [`${orgBase}/members`, `${orgBase}/groups`] },
    { nameKey: 'sidebar.orgSettings', href: `${orgBase}/settings`, icon: Cog6ToothIcon, matchPaths: [`${orgBase}/settings`, `${orgBase}/quotas`, `${orgBase}/plan`] },
  ] : [];
  const orgSectionActive = !!orgBase && location.pathname.startsWith(`${orgBase}/`);
  const [orgExpanded, setOrgExpanded] = useState(false);
  useEffect(() => {
    if (orgSectionActive) setOrgExpanded(true);
  }, [orgSectionActive]);

  const sidebarContent = (
    <div className="flex flex-col h-full bg-surface-1 border-r border-border-0">
      {/* Logo */}
      <div className="flex items-center h-[52px] px-4 border-b border-border-0 flex-shrink-0">
        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0">
          <img src={nubiecloudMark} alt="" className="h-8 w-8 object-contain flex-shrink-0" />
          {!collapsed && (
            <span className="text-[15px] font-bold text-text-primary truncate">
              NubieCloud
            </span>
          )}
        </Link>
      </div>

      {/* Organisation Selector */}
      {!collapsed && (
        <div className="px-3 py-3 border-b border-border-0">
          <OrganisationSelector />
        </div>
      )}

      {/* Main Navigation */}
      <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto">
        {mainNavItemDefs.map((item) => {
          const active = isActive(item);
          const isProjets = item.isProjects === true;
          const itemName = t(item.nameKey);

          return (
            <div key={item.nameKey}>
              <div className="flex items-center">
                {/* Chevron toggle for Projets (only when not collapsed) */}
                {isProjets && !collapsed && (
                  <button
                    onClick={handleToggleProjects}
                    className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-text-tertiary hover:text-text-secondary ml-0.5"
                    aria-label={projectsExpanded ? String(t('sidebar.collapseProjects')) : String(t('sidebar.expandProjects'))}
                  >
                    <ChevronRightIcon className={`w-3 h-3 transition-transform duration-150 ${projectsExpanded ? 'rotate-90' : ''}`} strokeWidth={2} />
                  </button>
                )}
                <Link
                  to={item.href}
                  title={collapsed ? itemName : undefined}
                  className={`
                    flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium
                    transition-colors duration-150 relative group flex-1
                    ${active
                      ? 'bg-blue-500/12 text-blue-400 font-semibold border-l-[3px] border-blue-500 pl-[7px]'
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface-3 border-l-[3px] border-transparent pl-[7px]'
                    }
                    ${isProjets && !collapsed ? 'ml-0' : ''}
                  `}
                  aria-current={active ? 'page' : undefined}
                >
                  <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-accent-400' : 'text-text-tertiary group-hover:text-text-secondary'}`} strokeWidth={1.6} />
                  {!collapsed && <span className="truncate">{itemName}</span>}
                </Link>
              </div>

              {/* Expandable project tree */}
              {isProjets && !collapsed && projectsExpanded && (
                <div className="ml-5 mt-0.5 space-y-px">
                  {loadingProjects ? (
                    <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-text-tertiary">
                      <div className="w-3 h-3 border border-text-tertiary border-t-transparent rounded-full animate-spin" />
                      {t('sidebar.loading')}
                    </div>
                  ) : projects.length === 0 ? (
                    <div className="px-2 py-1.5 text-[11px] text-text-tertiary italic">
                      {t('sidebar.noProject')}
                    </div>
                  ) : (
                    projects.map((project) => (
                      <div key={project.id}>
                        {/* Project row */}
                        <div className={`flex items-center gap-0.5 w-full px-2 py-1 rounded text-[11px] transition-colors ${location.pathname === `/projects/${project.id}` ? 'bg-blue-500/12 text-blue-400' : 'text-text-secondary hover:bg-surface-3'}`}>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleToggleProject(project.id); }}
                            className="flex-shrink-0 p-0.5 hover:text-text-primary"
                          >
                            <ChevronRightIcon className={`w-3 h-3 text-text-tertiary transition-transform duration-150 ${expandedProjectId === project.id ? 'rotate-90' : ''}`} strokeWidth={2} />
                          </button>
                          <button
                            onClick={() => { navigate(`/projects/${project.id}`); if (expandedProjectId !== project.id) handleToggleProject(project.id); }}
                            className="flex items-center gap-1.5 min-w-0 hover:text-text-primary transition-colors"
                          >
                            <FolderIcon className="w-3.5 h-3.5 flex-shrink-0 text-text-tertiary" strokeWidth={1.5} />
                            <span className="truncate">{project.nom}</span>
                          </button>
                        </div>

                        {/* Environments under project */}
                        {expandedProjectId === project.id && (
                          <div className="ml-4 space-y-px">
                            {loadingEnvs[project.id] ? (
                              <div className="flex items-center gap-2 px-2 py-1 text-[11px] text-text-tertiary">
                                <div className="w-2.5 h-2.5 border border-text-tertiary border-t-transparent rounded-full animate-spin" />
                                {t('sidebar.loading')}
                              </div>
                            ) : !projectEnvs[project.id] || projectEnvs[project.id].length === 0 ? (
                              <div className="px-2 py-1 text-[11px] text-text-tertiary italic">
                                {t('sidebar.noEnvironment')}
                              </div>
                            ) : (
                              projectEnvs[project.id].map((env) => {
                                const depCount = envDeployments[env.id]?.length;
                                return (
                                  <div key={env.id}>
                                    {/* Environment row */}
                                    <div className={`flex items-center gap-0.5 w-full px-2 py-1 rounded text-[11px] transition-colors ${location.pathname === `/deployments/${env.id}` ? 'bg-blue-500/12 text-blue-400' : 'text-text-secondary hover:bg-surface-3'}`}>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); handleToggleEnv(env.id); }}
                                        className="flex-shrink-0 p-0.5 hover:text-text-primary"
                                      >
                                        <ChevronRightIcon className={`w-3 h-3 text-text-tertiary transition-transform duration-150 ${expandedEnv === env.id ? 'rotate-90' : ''}`} strokeWidth={2} />
                                      </button>
                                      <button
                                        onClick={() => { navigate(`/deployments/${env.id}`); if (expandedEnv !== env.id) handleToggleEnv(env.id); }}
                                        className="flex items-center gap-1.5 min-w-0 hover:text-text-primary transition-colors"
                                      >
                                        <span className="truncate">{env.nom}</span>
                                      </button>
                                      {depCount !== undefined && (
                                        <span className="text-text-tertiary ml-auto text-[10px]">
                                          {depCount}
                                        </span>
                                      )}
                                    </div>

                                    {/* Deployments under environment */}
                                    {expandedEnv === env.id && (
                                      <div className="ml-4 space-y-px">
                                        {loadingDeps[env.id] ? (
                                          <div className="flex items-center gap-2 px-2 py-1 text-[11px] text-text-tertiary">
                                            <div className="w-2.5 h-2.5 border border-text-tertiary border-t-transparent rounded-full animate-spin" />
                                          </div>
                                        ) : !envDeployments[env.id] || envDeployments[env.id].length === 0 ? (
                                          <div className="px-2 py-1 text-[11px] text-text-tertiary italic">
                                            {t('sidebar.noApp')}
                                          </div>
                                        ) : (
                                          envDeployments[env.id].map((dep) => (
                                            <button
                                              key={dep.id}
                                              onClick={() => navigate(`/deployments/${dep.id}/manage`)}
                                              className={`flex items-center gap-1.5 w-full px-2 py-1 rounded text-[11px] transition-colors ${location.pathname === `/deployments/${dep.id}/manage` ? 'bg-blue-500/12 text-blue-400' : 'text-text-secondary hover:text-text-primary hover:bg-surface-3'}`}
                                            >
                                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getStatusDot(dep.statut_deploiement)}`} />
                                              <span className="truncate">{dep.nom}</span>
                                            </button>
                                          ))
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* Bottom Navigation */}
      <div className="px-2 py-3 border-t border-border-0 space-y-0.5">
        {orgBase && (
          <div>
            <div
              title={collapsed ? String(t('sidebar.organisation')) : undefined}
              className={`
                flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium
                transition-colors duration-150 group cursor-pointer
                ${orgSectionActive
                  ? 'bg-blue-500/12 text-blue-400 font-semibold border-l-[3px] border-blue-500 pl-[7px]'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-3 border-l-[3px] border-transparent pl-[7px]'
                }
              `}
              onClick={() => { if (collapsed) navigate(`${orgBase}/dashboard`); else setOrgExpanded(!orgExpanded); }}
            >
              <BuildingOfficeIcon className={`w-[18px] h-[18px] flex-shrink-0 ${orgSectionActive ? 'text-accent-400' : 'text-text-tertiary group-hover:text-text-secondary'}`} strokeWidth={1.6} />
              {!collapsed && (
                <>
                  <span className="truncate flex-1">{t('sidebar.organisation')}</span>
                  <ChevronDownIcon className={`w-3.5 h-3.5 text-text-tertiary transition-transform duration-150 ${orgExpanded ? '' : '-rotate-90'}`} strokeWidth={2} />
                </>
              )}
            </div>
            {!collapsed && orgExpanded && (
              <div className="ml-5 mt-0.5 space-y-px">
                {orgSubItems.map((sub) => {
                  const subActive = location.pathname === sub.href || (sub.matchPaths || []).some(p => location.pathname.startsWith(p));
                  return (
                    <Link
                      key={sub.nameKey}
                      to={sub.href}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded text-[12px] transition-colors ${subActive ? 'bg-blue-500/12 text-blue-400 font-medium' : 'text-text-secondary hover:text-text-primary hover:bg-surface-3'}`}
                    >
                      <sub.icon className="w-3.5 h-3.5 flex-shrink-0 text-text-tertiary" strokeWidth={1.5} />
                      <span className="truncate">{t(sub.nameKey)}</span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {bottomNavItems.map((item) => {
          const active = location.pathname === item.href;
          const itemName = t(item.nameKey);
          return (
            <Link
              key={item.nameKey}
              to={item.href}
              title={collapsed ? itemName : undefined}
              className={`
                flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium
                transition-colors duration-150 group
                ${active
                  ? 'bg-blue-500/12 text-blue-400 font-semibold border-l-[3px] border-blue-500 pl-[7px]'
                  : 'text-text-secondary hover:text-text-primary hover:bg-surface-3 border-l-[3px] border-transparent pl-[7px]'
                }
              `}
            >
              <item.icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-accent-400' : 'text-text-tertiary group-hover:text-text-secondary'}`} strokeWidth={1.6} />
              {!collapsed && <span className="truncate">{itemName}</span>}
            </Link>
          );
        })}
      </div>

      {/* Collapse Toggle */}
      <div className="hidden md:flex items-center justify-end px-3 py-2 border-t border-border-0">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-[22px] h-[22px] rounded-full bg-surface-3 border border-border-1 flex items-center justify-center hover:bg-surface-4 transition-colors"
          aria-label={collapsed ? String(t('sidebar.expandSidebar')) : String(t('sidebar.collapseSidebar'))}
        >
          {collapsed ? (
            <ChevronRightIcon className="w-3 h-3 text-text-tertiary" />
          ) : (
            <ChevronLeftIcon className="w-3 h-3 text-text-tertiary" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="md:hidden fixed top-3 left-3 z-50 p-1.5 rounded-lg bg-surface-1 border border-border-0 shadow-card"
        aria-label={String(t('sidebar.openMenu'))}
      >
        <Bars3Icon className="w-5 h-5 text-text-secondary" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute inset-y-0 left-0 w-[260px] z-50">
            <button
              onClick={() => setMobileOpen(false)}
              className="absolute top-3 right-3 z-50 p-1 rounded-lg hover:bg-surface-3"
              aria-label={String(t('sidebar.closeMenu'))}
            >
              <XMarkIcon className="w-5 h-5 text-text-secondary" />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <aside
        className={`hidden md:flex flex-shrink-0 transition-all duration-200 ${
          collapsed ? 'w-[62px]' : 'w-[230px]'
        }`}
      >
        {sidebarContent}
      </aside>
    </>
  );
};

export default Sidebar;
